import React, { useState, useEffect } from "react";
import { MessageCircle, Facebook, Mail, Stethoscope, Phone, PhoneForwarded, Globe, Bot, Copy, Check, ChevronDown, ChevronRight, Trash2, Loader2, AlertTriangle, Settings, ArrowLeft, Download, RefreshCw, Search, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import { useMasterData } from "@/hooks/use-master-data";
import JalpiConfigModal from "@/components/integrations/JalpiConfigModal";
import WhatsAppConfigModal from "@/components/integrations/WhatsAppConfigModal";

const INTEGRATIONS = [
  { name: "Website Forms", icon: Globe, desc: "Contact & book-appointment forms on dermatrixclinic.com post leads straight to the CRM, with campaign attribution", status: "Active", configurable: "website" },
  { name: "Website Chatbot", icon: Bot, desc: "Bot bookings, call-back requests & live-chat handoffs post straight to the CRM", status: "Active", configurable: "chatbot" },
  { name: "JustDial", icon: PhoneForwarded, desc: "Auto-capture leads from JustDial via webhook", status: "Active", configurable: "justdial" },
  { name: "Google Sheets + Email Parser", icon: Mail, desc: "Auto-extract leads from Google Ads emails and sync to CRM via Apps Script", status: "Active", configurable: "sheet-leads" },
  { name: "WhatsApp (Jalpi)", icon: MessageCircle, desc: "Send & receive WhatsApp messages. One number per branch, or shared across branches, with auto-lead capture.", status: "Active", configurable: "jalpi" },
  { name: "WhatsApp Business API", icon: MessageCircle, desc: "Meta Cloud API WhatsApp — send & receive messages and templates. Works alongside Jalpi.", status: "Active", configurable: "whatsapp-business" },
  { name: "Meta Lead Ads", icon: Facebook, desc: "Auto-sync leads from Facebook & Instagram campaigns", status: "Active", configurable: "meta" },
  { name: "Practo", icon: Stethoscope, desc: "Sync appointments and patient enquiries", status: "Coming Soon" },
  { name: "Call Center Dialer", icon: Phone, desc: "Click-to-call, auto-logging & call recordings via mobile app", status: "Active" },
];

export default function Integrations() {
  // The Meta tile's status is the one thing on this grid that can be false. Every
  // other entry is a static "Active" label, which was fine until a Facebook token
  // died: the tile kept saying Active while leads were being dropped, and the
  // truth was two clicks deep inside the modal. This pulls it to the surface.
  const [metaHealth, setMetaHealth] = useState(null);
  useEffect(() => {
    api.get("/integrations/meta/config")
      .then((r) => setMetaHealth(r.data))
      .catch(() => {});
  }, []);
  // A dead page token is not the only silent failure. A form config with a blank
  // or deactivated owner, and an ad form nobody configured, both kept importing
  // leads that landed with no owner — so they surface on the tile too.
  const tileStatus = (it) => (
    it.configurable === "meta" && (metaHealth?.pages_needing_attention || metaHealth?.forms_needing_attention)
      ? "Needs attention" : it.status
  );

  const [justDialOpen, setJustDialOpen] = useState(false);
  const [metaOpen, setMetaOpen] = useState(false);
  const [jalpiOpen, setJalpiOpen] = useState(false);
  const [whatsappBusinessOpen, setWhatsappBusinessOpen] = useState(false);
  const [sheetLeadsOpen, setSheetLeadsOpen] = useState(false);
  const [websiteOpen, setWebsiteOpen] = useState(false);
  const [chatbotOpen, setChatbotOpen] = useState(false);

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gold/30 bg-nude/50 p-5">
        <h3 className="font-display text-lg font-semibold text-navy">API-first & integration ready</h3>
        <p className="mt-1 text-sm text-muted-foreground">Dermatrix CRM exposes secure REST APIs (JWT auth) with webhook support. Connect your channels below.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {INTEGRATIONS.map((it) => {
          const status = tileStatus(it);
          return (
          <div key={it.name} className="flex flex-col rounded-xl border border-navy/10 bg-white p-5 shadow-sm" data-testid={`integration-${it.name.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between">
              <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${status === "Active" ? "bg-green-100 text-green-700" : status === "Needs attention" ? "bg-red-100 text-red-700" : "bg-navy/10 text-navy"}`}><it.icon className="h-5 w-5" /></div>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold
                ${status === "Active" ? "bg-green-100 text-green-800"
                  : status === "Needs attention" ? "bg-red-100 text-red-800"
                  : status === "Coming Soon" ? "bg-amber-100 text-amber-800"
                  : "bg-gray-100 text-gray-600"}`}>{status}</span>
            </div>
            <p className="mt-4 font-display font-semibold text-navy">{it.name}</p>
            <p className="mt-1 flex-1 text-sm text-muted-foreground">{it.desc}</p>
            {it.configurable ? (
              <button
                onClick={() => { if (it.configurable === "meta") setMetaOpen(true); else if (it.configurable === "jalpi") setJalpiOpen(true); else if (it.configurable === "whatsapp-business") setWhatsappBusinessOpen(true); else if (it.configurable === "sheet-leads") setSheetLeadsOpen(true); else if (it.configurable === "website") setWebsiteOpen(true); else if (it.configurable === "chatbot") setChatbotOpen(true); else setJustDialOpen(true); }}
                className="mt-4 rounded-lg border border-green-300 bg-green-50 py-2 text-sm font-medium text-green-700 hover:bg-green-100 transition-colors"
              >
                Configure
              </button>
            ) : (
              <button disabled className="mt-4 cursor-not-allowed rounded-lg border border-navy/20 py-2 text-sm font-medium text-muted-foreground">
                {status === "Active" ? "Configured" : "Configure"}
              </button>
            )}
          </div>
          );
        })}
      </div>

      <JustDialConfigDialog open={justDialOpen} onOpenChange={setJustDialOpen} />
      <MetaOAuthDialog open={metaOpen} onOpenChange={setMetaOpen} />
      <JalpiConfigModal open={jalpiOpen} onOpenChange={setJalpiOpen} />
      <WhatsAppConfigModal open={whatsappBusinessOpen} onOpenChange={setWhatsappBusinessOpen} />
      <SheetLeadsConfigDialog open={sheetLeadsOpen} onOpenChange={setSheetLeadsOpen} />
      <WebhookIngestDialog
        open={websiteOpen}
        onOpenChange={setWebsiteOpen}
        title="Website Forms → Dermatrix CRM"
        icon={Globe}
        source="website"
        envVar="WEBSITE_API_KEY"
        assignmentLabel="the website forms"
        note="The site posts every contact / book-appointment submission here the moment it's saved, so leads no longer wait on email delivery. If the same person enquires again, their existing lead is reopened with a repeat badge instead of duplicating."
        samplePayload={{
          external_id: "website:contact:123",
          name: "", last_name: "", phone: "", email: "",
          city: "", service: "", message: "",
          form_name: "contact-us", page_url: "",
          utm_source: "", utm_medium: "", utm_campaign: "",
          utm_term: "", utm_content: "", gclid: "", fbclid: "",
        }}
      />
      <WebhookIngestDialog
        open={chatbotOpen}
        onOpenChange={setChatbotOpen}
        title="Website Chatbot → Dermatrix CRM"
        icon={Bot}
        source="chatbot"
        envVar="CHATBOT_API_KEY"
        assignmentLabel="the chatbot"
        note="Covers all three capture points — bot bookings, call-back requests and live-chat handoffs. A bot booking arrives as a New Lead with the requested slot in the remark; the CRM team books the actual appointment."
        samplePayload={{
          external_id: "chatbot:lead:123",
          name: "", phone: "", email: "",
          location: "", service: "",
          lead_type: "appointment | call_request | live_chat",
          preferred_date: "", preferred_time: "", session_id: "",
        }}
      />
    </div>
  );
}

function JustDialConfigDialog({ open, onOpenChange }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);
  const [method, setMethod] = useState("round_robin");
  const [assignedTo, setAssignedTo] = useState("");
  const [branchId, setBranchId] = useState("");
  const [franchiseId, setFranchiseId] = useState("");
  const [telecallers, setTelecallers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get("/integrations/justdial/config"),
      api.get("/users", { params: { role: "telecaller" } }).catch(() => ({ data: [] })),
      api.get("/branches").catch(() => ({ data: [] })),
      api.get("/franchises").catch(() => ({ data: [] })),
    ])
      .then(([r, tc, br, fr]) => {
        setConfig(r.data);
        setMethod(r.data.assignment_method || "round_robin");
        setAssignedTo(r.data.assigned_to || "");
        setBranchId(r.data.branch_id || "");
        setFranchiseId(r.data.franchise_id || "");
        setTelecallers((tc.data || []).filter((u) => u.active !== false));
        setBranches(br.data || []);
        setFranchises(fr.data || []);
      })
      .catch(() => toast.error("Failed to load config"))
      .finally(() => setLoading(false));
  }, [open]);

  const saveAssignment = async () => {
    if (method === "manual" && !assignedTo) return toast.error("Select a tele-caller");
    if (method === "branch_based" && !branchId) return toast.error("Select a branch");
    if (method === "franchise_based" && !franchiseId) return toast.error("Select a franchise");
    setSaving(true);
    try {
      await api.put("/integrations/justdial/config", {
        assignment_method: method,
        assigned_to: method === "manual" ? assignedTo : null,
        branch_id: method === "branch_based" ? branchId : null,
        franchise_id: method === "franchise_based" ? franchiseId : null,
      });
      toast.success("Assignment settings saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const webhookUrl = config?.path ? `${window.location.origin}${config.path}` : "";
  const curlCmd = webhookUrl ? `curl -k -X POST ${webhookUrl} \\
  --header 'Content-Type: application/json' \\
  --data '{
    "name": "",
    "mobile": "",
    "phone": "",
    "email": "",
    "date": "",
    "category": "",
    "city": ""
  }'` : "";

  const copyToClipboard = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setter(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="font-display text-navy flex items-center gap-2">
            <PhoneForwarded className="h-5 w-5" /> Connect JustDial to Dermatrix CRM
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="py-8 text-center text-muted-foreground">Loading…</p>
        ) : !config?.configured ? (
          <p className="py-8 text-center text-muted-foreground">JustDial API key not configured. Add <code className="text-xs bg-cream px-1 rounded">JUSTDIAL_API_KEY</code> to your server .env file.</p>
        ) : (
          <div className="space-y-5 py-2 overflow-y-auto flex-1 -mx-6 px-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-navy">1. Copy the Webhook URL below.</p>
              <p className="text-sm font-medium text-navy">2. Share the below Curl code with the JustDial team.</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Webhook URL</p>
                <button
                  onClick={() => copyToClipboard(webhookUrl, setCopiedUrl)}
                  className="flex items-center gap-1 text-xs text-navy hover:underline"
                >
                  {copiedUrl ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copiedUrl ? "Copied" : "Copy"}
                </button>
              </div>
              <div className="rounded-lg border border-navy/10 bg-cream/50 p-3">
                <code className="text-xs text-ink break-all select-all">{webhookUrl}</code>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Curl Command</p>
                <button
                  onClick={() => copyToClipboard(curlCmd, setCopiedCurl)}
                  className="flex items-center gap-1 text-xs text-navy hover:underline"
                >
                  {copiedCurl ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copiedCurl ? "Copied" : "Copy"}
                </button>
              </div>
              <pre className="rounded-lg border border-navy/10 bg-cream/50 p-3 text-xs text-ink overflow-x-auto whitespace-pre-wrap select-all font-mono">
                {curlCmd}
              </pre>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-amber-800">
                <span className="font-semibold">Note:</span> Each lead received via this webhook will automatically appear in the CRM. If the same person enquires again, their existing lead will be reopened with a repeat badge.
              </p>
            </div>

            <div className="border-t border-navy/10 pt-4 space-y-4">
              <p className="text-sm font-medium text-navy">Auto-assignment</p>
              <p className="text-sm text-muted-foreground">
                Choose how new leads coming in from JustDial are auto-assigned to tele-callers.
              </p>

              <div className="space-y-1.5">
                <Label className="text-sm text-navy">Assignment Method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>
                    {ASSIGNMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {method === "manual" && (
                <div className="space-y-1.5">
                  <Label className="text-sm text-navy">Tele-caller</Label>
                  <Select value={assignedTo} onValueChange={setAssignedTo}>
                    <SelectTrigger><SelectValue placeholder="Select tele-caller" /></SelectTrigger>
                    <SelectContent>
                      {telecallers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {method === "branch_based" && (
                <div className="space-y-1.5">
                  <Label className="text-sm text-navy">Branch</Label>
                  <Select value={branchId} onValueChange={setBranchId}>
                    <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {method === "franchise_based" && (
                <div className="space-y-1.5">
                  <Label className="text-sm text-navy">Franchise</Label>
                  <Select value={franchiseId} onValueChange={setFranchiseId}>
                    <SelectTrigger><SelectValue placeholder="Select franchise" /></SelectTrigger>
                    <SelectContent>
                      {franchises.map((f) => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="shrink-0 border-t border-navy/10 pt-3">
          {config?.configured ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-navy/20 text-navy hover:bg-navy/5">Cancel</Button>
              <Button onClick={saveAssignment} disabled={saving || loading} className="bg-navy text-white hover:bg-navy-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} className="bg-navy text-white hover:bg-navy-hover">Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Radix Select can't hold an empty-string value, so "no default branch" needs a
// sentinel. It is translated back to null before the config is saved.
const NO_DEFAULT_BRANCH = "__none__";

const ASSIGNMENT_METHODS = [
  { value: "manual", label: "Manual (to a tele-caller)" },
  { value: "round_robin", label: "Round Robin" },
  { value: "branch_based", label: "Branch Based" },
  { value: "franchise_based", label: "Franchise Based" },
  { value: "language_based", label: "Language Based" },
];

/**
 * Config dialog for a first-party single-lead webhook (website forms, chatbot).
 * These sources all speak the same shape — API-key path, sample payload,
 * per-source auto-assignment — so one component covers them instead of a
 * near-identical copy each. `source` is the URL segment: /integrations/<source>/config.
 */
function WebhookIngestDialog({ open, onOpenChange, title, icon: Icon, source, envVar,
                               assignmentLabel, note, samplePayload }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);
  const [method, setMethod] = useState("round_robin");
  const [assignedTo, setAssignedTo] = useState("");
  const [branchId, setBranchId] = useState("");
  const [franchiseId, setFranchiseId] = useState("");
  // Optional default branch stamped on every lead from this source. "" = leave
  // the lead's branch empty for the tele-caller to set on the first call.
  const [defaultBranchId, setDefaultBranchId] = useState("");
  // Per-clinic routing, held as rows rather than an object so a half-typed city
  // doesn't lose its selected branch while the admin is still editing it.
  const [cityRows, setCityRows] = useState([]);
  const [telecallers, setTelecallers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get(`/integrations/${source}/config`),
      api.get("/users", { params: { role: "telecaller" } }).catch(() => ({ data: [] })),
      api.get("/branches").catch(() => ({ data: [] })),
      api.get("/franchises").catch(() => ({ data: [] })),
    ])
      .then(([r, tc, br, fr]) => {
        setConfig(r.data);
        setMethod(r.data.assignment_method || "round_robin");
        setAssignedTo(r.data.assigned_to || "");
        setBranchId(r.data.branch_id || "");
        setFranchiseId(r.data.franchise_id || "");
        setDefaultBranchId(r.data.default_branch_id || NO_DEFAULT_BRANCH);
        setCityRows(Object.entries(r.data.city_branch_map || {}).map(([city, branchId]) => ({ city, branchId })));
        setTelecallers((tc.data || []).filter((u) => u.active !== false));
        setBranches(br.data || []);
        setFranchises(fr.data || []);
      })
      .catch(() => toast.error("Failed to load config"))
      .finally(() => setLoading(false));
  }, [open, source]);

  const saveAssignment = async () => {
    if (method === "manual" && !assignedTo) return toast.error("Select a tele-caller");
    if (method === "branch_based" && !branchId) return toast.error("Select a branch");
    if (method === "franchise_based" && !franchiseId) return toast.error("Select a franchise");
    setSaving(true);
    try {
      await api.put(`/integrations/${source}/config`, {
        assignment_method: method,
        assigned_to: method === "manual" ? assignedTo : null,
        branch_id: method === "branch_based" ? branchId : null,
        franchise_id: method === "franchise_based" ? franchiseId : null,
        default_branch_id: defaultBranchId === NO_DEFAULT_BRANCH ? null : defaultBranchId,
        // Half-filled rows are dropped here and again on the server — an admin
        // mid-edit shouldn't be blocked from saving the rest.
        city_branch_map: cityRows.reduce((acc, r) => {
          const city = (r.city || "").trim();
          if (city && r.branchId) acc[city] = r.branchId;
          return acc;
        }, {}),
      });
      toast.success("Assignment settings saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const webhookUrl = config?.path ? `${window.location.origin}${config.path}` : "";
  const curlCmd = webhookUrl ? `curl -X POST ${webhookUrl} \\
  --header 'Content-Type: application/json' \\
  --data '${JSON.stringify(samplePayload, null, 2)}'` : "";

  const copyToClipboard = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setter(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="font-display text-navy flex items-center gap-2">
            <Icon className="h-5 w-5" /> {title}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="py-8 text-center text-muted-foreground">Loading…</p>
        ) : !config?.configured ? (
          <p className="py-8 text-center text-muted-foreground">
            Webhook key not configured. Add <code className="text-xs bg-cream px-1 rounded">{envVar}</code> to your server .env file, then set the same value on the source.
          </p>
        ) : (
          <div className="space-y-5 py-2 overflow-y-auto flex-1 -mx-6 px-6">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Webhook URL</p>
                <button
                  onClick={() => copyToClipboard(webhookUrl, setCopiedUrl)}
                  className="flex items-center gap-1 text-xs text-navy hover:underline"
                >
                  {copiedUrl ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copiedUrl ? "Copied" : "Copy"}
                </button>
              </div>
              <div className="rounded-lg border border-navy/10 bg-cream/50 p-3">
                <code className="text-xs text-ink break-all select-all">{webhookUrl}</code>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Payload</p>
                <button
                  onClick={() => copyToClipboard(curlCmd, setCopiedCurl)}
                  className="flex items-center gap-1 text-xs text-navy hover:underline"
                >
                  {copiedCurl ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copiedCurl ? "Copied" : "Copy"}
                </button>
              </div>
              <pre className="rounded-lg border border-navy/10 bg-cream/50 p-3 text-xs text-ink overflow-x-auto whitespace-pre-wrap select-all font-mono">
                {curlCmd}
              </pre>
              <p className="mt-1.5 text-xs text-muted-foreground">
                <code className="bg-cream px-1 rounded">external_id</code> makes retries safe — re-sending the same id is ignored rather than creating a second lead.
              </p>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-amber-800"><span className="font-semibold">Note:</span> {note}</p>
            </div>

            <div className="border-t border-navy/10 pt-4 space-y-4">
              <p className="text-sm font-medium text-navy">Auto-assignment</p>
              <p className="text-sm text-muted-foreground">
                Choose how new leads coming in from {assignmentLabel} are auto-assigned to tele-callers.
              </p>

              <div className="space-y-1.5">
                <Label className="text-sm text-navy">Assignment Method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>
                    {ASSIGNMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {method === "manual" && (
                <div className="space-y-1.5">
                  <Label className="text-sm text-navy">Tele-caller</Label>
                  <Select value={assignedTo} onValueChange={setAssignedTo}>
                    <SelectTrigger><SelectValue placeholder="Select tele-caller" /></SelectTrigger>
                    <SelectContent>
                      {telecallers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {method === "branch_based" && (
                <div className="space-y-1.5">
                  <Label className="text-sm text-navy">Branch</Label>
                  <Select value={branchId} onValueChange={setBranchId}>
                    <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {method === "franchise_based" && (
                <div className="space-y-1.5">
                  <Label className="text-sm text-navy">Franchise</Label>
                  <Select value={franchiseId} onValueChange={setFranchiseId}>
                    <SelectTrigger><SelectValue placeholder="Select franchise" /></SelectTrigger>
                    <SelectContent>
                      {franchises.map((f) => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="border-t border-navy/10 pt-4 space-y-4">
              <p className="text-sm font-medium text-navy">Branch <span className="font-normal text-muted-foreground">(optional)</span></p>
              <p className="text-sm text-muted-foreground">
                The form's city dropdown is built from the clinic list, so the value below is the
                centre the visitor picked. Map each one to route leads to the right branch. Anything
                unmapped falls back to the default branch.
              </p>

              <div className="space-y-2">
                <Label className="text-sm text-navy">Clinic to branch</Label>
                {cityRows.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No mappings yet — every lead uses the default branch below.
                  </p>
                )}
                {cityRows.map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={row.city}
                      onChange={(e) => setCityRows((rows) => rows.map((r, j) => (j === i ? { ...r, city: e.target.value } : r)))}
                      placeholder="City value from the form"
                      className="flex-1"
                    />
                    <Select
                      value={row.branchId}
                      onValueChange={(v) => setCityRows((rows) => rows.map((r, j) => (j === i ? { ...r, branchId: v } : r)))}
                    >
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Select branch" /></SelectTrigger>
                      <SelectContent>
                        {branches.map((b) => (
                          <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => setCityRows((rows) => rows.filter((_, j) => j !== i))}
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      aria-label={`Remove mapping for ${row.city || "blank city"}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline" size="sm"
                  onClick={() => setCityRows((rows) => [...rows, { city: "", branchId: "" }])}
                  className="border-navy/20 text-navy hover:bg-navy/5"
                >
                  Add mapping
                </Button>
                <p className="text-xs text-muted-foreground">
                  Type the value exactly as the form sends it. Case, spaces and punctuation are
                  ignored when matching, but <span className="font-medium text-navy">Manpada</span> and{" "}
                  <span className="font-medium text-navy">Manpada Mumbai</span> count as different
                  values, so give each its own row.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm text-navy">Default Branch</Label>
                <Select value={defaultBranchId} onValueChange={setDefaultBranchId}>
                  <SelectTrigger><SelectValue placeholder="Set later (recommended)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_DEFAULT_BRANCH}>Set later (recommended)</SelectItem>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Used when the city is blank or unmapped. Leave as Set later to have the
                  tele-caller set it on the first call.
                </p>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="shrink-0 border-t border-navy/10 pt-3">
          {config?.configured ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-navy/20 text-navy hover:bg-navy/5">Cancel</Button>
              <Button onClick={saveAssignment} disabled={saving || loading} className="bg-navy text-white hover:bg-navy-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} className="bg-navy text-white hover:bg-navy-hover">Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SheetLeadsConfigDialog({ open, onOpenChange }) {
  const [method, setMethod] = useState("round_robin");
  const [assignedTo, setAssignedTo] = useState("");
  const [branchId, setBranchId] = useState("");
  const [franchiseId, setFranchiseId] = useState("");
  const [telecallers, setTelecallers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get("/integrations/sheet-leads/config"),
      api.get("/users", { params: { role: "telecaller" } }).catch(() => ({ data: [] })),
      api.get("/branches").catch(() => ({ data: [] })),
      api.get("/franchises").catch(() => ({ data: [] })),
    ])
      .then(([cfg, tc, br, fr]) => {
        setMethod(cfg.data.assignment_method || "round_robin");
        setAssignedTo(cfg.data.assigned_to || "");
        setBranchId(cfg.data.branch_id || "");
        setFranchiseId(cfg.data.franchise_id || "");
        setTelecallers((tc.data || []).filter((u) => u.active !== false));
        setBranches(br.data || []);
        setFranchises(fr.data || []);
      })
      .catch(() => toast.error("Failed to load config"))
      .finally(() => setLoading(false));
  }, [open]);

  const save = async () => {
    if (method === "manual" && !assignedTo) return toast.error("Select a tele-caller");
    if (method === "branch_based" && !branchId) return toast.error("Select a branch");
    if (method === "franchise_based" && !franchiseId) return toast.error("Select a franchise");
    setSaving(true);
    try {
      await api.put("/integrations/sheet-leads/config", {
        assignment_method: method,
        assigned_to: method === "manual" ? assignedTo : null,
        branch_id: method === "branch_based" ? branchId : null,
        franchise_id: method === "franchise_based" ? franchiseId : null,
      });
      toast.success("Assignment settings saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-navy flex items-center gap-2">
            <Mail className="h-5 w-5" /> Google Sheets + Email Parser
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="py-8 text-center text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Choose how leads pulled from the sheet are auto-assigned to tele-callers.
            </p>

            <div className="space-y-1.5">
              <Label className="text-sm text-navy">Assignment Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  {ASSIGNMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {method === "manual" && (
              <div className="space-y-1.5">
                <Label className="text-sm text-navy">Tele-caller</Label>
                <Select value={assignedTo} onValueChange={setAssignedTo}>
                  <SelectTrigger><SelectValue placeholder="Select tele-caller" /></SelectTrigger>
                  <SelectContent>
                    {telecallers.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {method === "branch_based" && (
              <div className="space-y-1.5">
                <Label className="text-sm text-navy">Branch</Label>
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {method === "franchise_based" && (
              <div className="space-y-1.5">
                <Label className="text-sm text-navy">Franchise</Label>
                <Select value={franchiseId} onValueChange={setFranchiseId}>
                  <SelectTrigger><SelectValue placeholder="Select franchise" /></SelectTrigger>
                  <SelectContent>
                    {franchises.map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-amber-800">
                <span className="font-semibold">Note:</span> Manual sends every sheet lead to the chosen tele-caller. Round Robin distributes new leads evenly across active tele-callers. Language Based matches the lead's language when available and falls back to even distribution.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-navy/20 text-navy hover:bg-navy/5">Cancel</Button>
          <Button onClick={save} disabled={saving || loading} className="bg-navy text-white hover:bg-navy-hover">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const CRM_FIELDS = [
  { value: "name", label: "Name" },
  { value: "mobile", label: "Mobile" },
  { value: "email", label: "Email" },
  { value: "city", label: "City" },
  { value: "state", label: "State" },
  { value: "wants_visit", label: "Wants to Visit" },
  { value: "interested_service", label: "Interested Service" },
  { value: "concern", label: "Concern" },
  { value: "category", label: "Category" },
  { value: "remark", label: "Remark" },
  { value: "notes", label: "Notes" },
];

const AUTO_MAP = {
  full_name: "name",
  first_name: "name",
  last_name: "name",
  phone_number: "mobile",
  phone: "mobile",
  email: "email",
  city: "city",
  state: "state",
};

function humanizeFieldName(name) {
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}


function MetaOAuthDialog({ open, onOpenChange }) {
  const [config, setConfig] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [expandedPage, setExpandedPage] = useState(null);
  const [campaigns, setCampaigns] = useState({});
  const [campaignsLoading, setCampaignsLoading] = useState({});
  const [parkedLeads, setParkedLeads] = useState({});   // page_id -> count
  const [retrying, setRetrying] = useState(false);

  // Form config navigation: "pages" | "forms" | "config"
  const [view, setView] = useState("pages");
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);

  const loadData = () => {
    setLoading(true);
    Promise.all([
      api.get("/integrations/meta/config"),
      api.get("/integrations/meta/pages").catch(() => ({ data: [] })),
      // Leads Meta announced that we could not fetch — almost always a dead
      // token. Shown per page so the cost of leaving it broken is visible.
      api.get("/integrations/meta/failed-leads").catch(() => ({ data: [] })),
    ])
      .then(([configRes, pagesRes, failedRes]) => {
        setConfig(configRes.data);
        setPages(pagesRes.data);
        const byPage = {};
        (failedRes.data || []).forEach((f) => {
          byPage[f.page_id] = (byPage[f.page_id] || 0) + 1;
        });
        setParkedLeads(byPage);
      })
      .catch(() => toast.error("Failed to load config"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) {
      loadData();
      setView("pages");
      setSelectedPage(null);
      setSelectedForm(null);
      setEditingConfig(null);
    }
  }, [open]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/integrations/meta/oauth/start");
      window.location.href = data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start Facebook connection");
      setConnecting(false);
    }
  };

  const handleRetryParked = async (pageId) => {
    setRetrying(true);
    try {
      const { data } = await api.post(
        `/integrations/meta/failed-leads/retry?page_id=${pageId}`);
      if (data.recovered) toast.success(`Imported ${data.recovered} lead(s)`);
      if (data.still_failing) {
        toast.error(`${data.still_failing} still could not be fetched — reconnect the page first`);
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(false);
    }
  };

  const handleDisconnect = async (pageId, pageName) => {
    if (!window.confirm(`Disconnect "${pageName}"? Leads from this page will stop syncing.`)) return;
    try {
      await api.delete(`/integrations/meta/pages/${pageId}`);
      toast.success(`Disconnected ${pageName}`);
      loadData();
    } catch {
      toast.error("Failed to disconnect page");
    }
  };

  const toggleCampaigns = async (pageId) => {
    if (expandedPage === pageId) {
      setExpandedPage(null);
      return;
    }
    setExpandedPage(pageId);
    if (campaigns[pageId]) return;

    setCampaignsLoading((prev) => ({ ...prev, [pageId]: true }));
    try {
      const { data } = await api.get(`/integrations/meta/pages/${pageId}/campaigns`);
      setCampaigns((prev) => ({ ...prev, [pageId]: data }));
    } catch {
      toast.error("Failed to load campaigns");
    } finally {
      setCampaignsLoading((prev) => ({ ...prev, [pageId]: false }));
    }
  };

  const openFormsView = (page) => {
    setSelectedPage(page);
    setView("forms");
  };

  const openConfigWizard = (form, existingConfig) => {
    setSelectedForm(form);
    setEditingConfig(existingConfig || null);
    setView("config");
  };

  const hasPages = pages.length > 0;

  const dialogTitle = view === "pages" ? "Meta Lead Ads"
    : view === "forms" ? `Lead Forms — ${selectedPage?.page_name}`
    : `Configure Form`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-navy flex items-center gap-2">
            {view !== "pages" && (
              <button
                onClick={() => setView(view === "config" ? "forms" : "pages")}
                className="rounded p-1 hover:bg-cream/80 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <Facebook className="h-5 w-5" /> {dialogTitle}
          </DialogTitle>
        </DialogHeader>

        {view === "pages" && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-navy" />
              </div>
            ) : config?.oauth_available ? (
              <div className="space-y-5 py-2">
                {/* Misrouted-lead warnings. A form with no owner, or one whose owner
                    was deactivated, silently imported leads nobody was assigned to
                    for weeks. The backend now falls back to the tele-caller pool so
                    nothing is lost, but the config is still wrong — say so here
                    instead of leaving it to be spotted in the Leads table. */}
                {(config?.broken_form_configs?.length > 0 || config?.unconfigured_forms?.length > 0) && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2">
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      <p className="text-sm font-semibold">Lead routing needs attention</p>
                    </div>
                    {config.broken_form_configs?.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs text-amber-900">
                          These forms have no usable owner — their leads are being shared out across
                          active tele-callers instead:
                        </p>
                        <ul className="space-y-0.5 text-xs text-amber-900">
                          {config.broken_form_configs.map((f) => (
                            <li key={f.form_id} className="truncate">
                              • <strong>{f.form_name || f.form_id}</strong> — {f.reason}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {config.unconfigured_forms?.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs text-amber-900">
                          These ad forms have sent leads but were never configured:
                        </p>
                        <ul className="space-y-0.5 text-xs text-amber-900">
                          {config.unconfigured_forms.map((f) => (
                            <li key={f.form_id} className="truncate">
                              • <strong>{f.form_name || f.form_id}</strong> — {f.lead_count} lead(s), last {f.last_seen_at?.slice(0, 10)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {hasPages && (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Connected Pages</p>
                    {pages.map((page) => (
                      <div key={page.page_id} className="rounded-lg border border-navy/10 bg-white">
                        <div className="flex items-center justify-between p-3">
                          <button onClick={() => toggleCampaigns(page.page_id)} className="flex items-center gap-2 text-left flex-1 min-w-0">
                            {expandedPage === page.page_id ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                            <Facebook className="h-4 w-4 text-blue-600 shrink-0" />
                            <span className="text-sm font-medium text-navy truncate">{page.page_name}</span>
                            {/* Reads token_status, not status. `status` used to
                                double as token health and was flipped by a
                                nightly job comparing the wrong token's expiry —
                                so this badge said "Token Expired" on tokens that
                                still worked. Health is now Meta's verdict. */}
                            <span className={`ml-2 shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
                              page.token_status === "invalid" ? "bg-red-100 text-red-800"
                              : page.token_status === "valid" ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-600"
                            }`}>
                              {page.token_status === "invalid" ? "Reconnect needed"
                                : page.token_status === "valid" ? "Active" : "Not checked yet"}
                            </span>
                          </button>
                          <div className="flex items-center gap-1 ml-2 shrink-0">
                            <button
                              onClick={() => openFormsView(page)}
                              className="rounded p-1.5 text-navy/60 hover:bg-cream hover:text-navy transition-colors"
                              title="Configure Lead Forms"
                            >
                              <Settings className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDisconnect(page.page_id, page.page_name)}
                              className="rounded p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                              title="Disconnect"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {(page.token_status === "invalid" || parkedLeads[page.page_id]) && (
                          <div className="mx-3 mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600 shrink-0" />
                              <div className="min-w-0">
                                {page.token_status === "invalid" && (
                                  <p className="text-xs text-amber-800">
                                    Facebook has rejected this page's access token, so new lead-form
                                    submissions are not reaching the CRM. Click "Connect More Pages"
                                    below and sign in with an account that administers this page.
                                  </p>
                                )}
                                {!!parkedLeads[page.page_id] && (
                                  <p className="mt-1 text-xs font-medium text-amber-900">
                                    {parkedLeads[page.page_id]} lead(s) could not be imported and are
                                    waiting to be retried.
                                  </p>
                                )}
                              </div>
                            </div>
                            {!!parkedLeads[page.page_id] && (
                              <button
                                onClick={() => handleRetryParked(page.page_id)}
                                disabled={retrying}
                                className="mt-2 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                              >
                                {retrying ? "Retrying…" : "Retry these leads"}
                              </button>
                            )}
                          </div>
                        )}

                        {expandedPage === page.page_id && (
                          <div className="border-t border-navy/10 px-3 pb-3 pt-2">
                            {campaignsLoading[page.page_id] ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                <span className="ml-2 text-xs text-muted-foreground">Loading campaigns...</span>
                              </div>
                            ) : campaigns[page.page_id] ? (
                              <CampaignsList data={campaigns[page.page_id]} />
                            ) : null}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {hasPages && (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                    <p className="text-xs text-green-800">
                      <span className="font-semibold">How it works:</span> When someone fills out a lead form on your Facebook/Instagram ad, the lead appears in your CRM automatically. Click the <Settings className="inline h-3 w-3" /> icon on a page to configure individual forms.
                    </p>
                  </div>
                )}

                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#1877F2] py-2.5 text-sm font-semibold text-white hover:bg-[#166FE5] transition-colors disabled:opacity-60"
                >
                  {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Facebook className="h-4 w-4" />}
                  {hasPages ? "Connect More Pages" : "Connect with Facebook"}
                </button>

                {!hasPages && (
                  <p className="text-center text-xs text-muted-foreground">
                    Click above to connect your Facebook Pages. We'll automatically set up lead syncing — no manual configuration needed.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <p className="text-sm text-muted-foreground">Facebook Login is not configured yet. Add these environment variables to your server <code className="text-xs bg-cream px-1 rounded">.env</code> file:</p>
                <pre className="rounded-lg border border-navy/10 bg-cream/50 p-3 text-xs font-mono whitespace-pre-wrap">{`META_APP_ID=your_facebook_app_id
META_APP_SECRET=your_app_secret`}</pre>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs text-amber-800"><span className="font-semibold">How to get these:</span></p>
                  <ol className="mt-1 text-xs text-amber-800 list-decimal pl-4 space-y-1">
                    <li><strong>META_APP_ID</strong> — From Meta Developer Portal → Your App → Settings → Basic → App ID</li>
                    <li><strong>META_APP_SECRET</strong> — From Meta Developer Portal → Your App → Settings → Basic → App Secret</li>
                  </ol>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} className="bg-navy text-white hover:bg-navy-hover">Close</Button>
            </DialogFooter>
          </>
        )}

        {view === "forms" && selectedPage && (
          <FormsListView
            page={selectedPage}
            onConfigureForm={openConfigWizard}
            onBack={() => setView("pages")}
          />
        )}

        {view === "config" && selectedPage && selectedForm && (
          <FormConfigWizard
            page={selectedPage}
            form={selectedForm}
            existingConfig={editingConfig}
            onSaved={() => {
              setView("forms");
              setEditingConfig(null);
            }}
            onBack={() => setView("forms")}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}


function FormLeadBadge({ form }) {
  const n = form?.leads_count || 0;
  if (!n) return null;
  return (
    <span className="shrink-0 rounded-full bg-navy/10 px-2 py-0.5 text-[10px] font-medium text-navy">
      {n} lead{n === 1 ? "" : "s"}
    </span>
  );
}


function FormsListView({ page, onConfigureForm, onBack }) {
  const [forms, setForms] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadForms = async () => {
    setLoading(true);
    setError(null);
    try {
      const [formsRes, configsRes] = await Promise.all([
        api.get(`/integrations/meta/pages/${page.page_id}/forms`),
        api.get(`/integrations/meta/form-configs`, { params: { page_id: page.page_id } }),
      ]);
      setForms(formsRes.data);
      setConfigs(configsRes.data);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to load forms";
      if (err.response?.status === 401) {
        setError("token_expired");
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadForms(); }, [page.page_id]);

  const configByFormId = {};
  configs.forEach((c) => { configByFormId[c.form_id] = c; });

  // Search box — filter forms by name (100+ forms make this essential).
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const matches = (name) => !q || (name || "").toLowerCase().includes(q);

  const configuredForms = forms.filter((f) => configByFormId[f.id] && matches(f.name));
  const unconfiguredForms = forms.filter((f) => !configByFormId[f.id] && matches(f.name));

  // Configs for forms no longer on Facebook
  const orphanedConfigs = configs.filter(
    (c) => !forms.find((f) => f.id === c.form_id) && matches(c.form_name)
  );

  const handleDelete = async (configId, formName) => {
    if (!window.confirm(`Remove configuration for "${formName}"? Leads will use default settings.`)) return;
    try {
      await api.delete(`/integrations/meta/form-configs/${configId}`);
      toast.success("Form configuration removed");
      loadForms();
    } catch {
      toast.error("Failed to remove configuration");
    }
  };

  const [syncOpen, setSyncOpen] = useState(false);

  const [exportingId, setExportingId] = useState(null);
  const handleExportLeads = async (formId, formName) => {
    setExportingId(formId);
    try {
      const res = await api.get(`/integrations/meta/forms/${formId}/export`, {
        params: { page_id: page.page_id, days: 0 },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (formName || formId).replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 60);
      a.download = `meta_leads_${safe}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Leads exported");
    } catch (err) {
      const detail = err.response?.status === 401
        ? "Page token expired — please reconnect this page"
        : "Export failed";
      toast.error(detail);
    } finally {
      setExportingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-navy" />
      </div>
    );
  }

  if (error === "token_expired") {
    return (
      <div className="space-y-4 py-4">
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Page token expired</p>
            <p className="text-xs text-amber-700 mt-1">Please reconnect this page from the main screen to refresh the token.</p>
          </div>
        </div>
        <Button onClick={onBack} variant="outline" className="w-full">Back to Pages</Button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 py-4">
        <p className="text-center text-sm text-red-600">{error}</p>
        <Button onClick={onBack} variant="outline" className="w-full">Back to Pages</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 py-2">
      <div className="flex items-center justify-between gap-2 rounded-lg border border-navy/10 bg-navy/5 p-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-navy">Sync missed leads</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Pull leads Facebook collected but didn't deliver. Choose date range, forms, owner. Safe to re-run — no duplicates.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="text-xs h-8 shrink-0"
          onClick={() => setSyncOpen(true)}
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Sync…
        </Button>
      </div>

      <SyncMissedDialog
        open={syncOpen}
        onOpenChange={setSyncOpen}
        pageId={page.page_id}
        forms={forms}
        configuredFormIds={configuredForms.map((f) => f.id)}
      />

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search forms by name…"
          className="h-9 pl-8 pr-8 text-sm"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-navy"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <Tabs defaultValue={configuredForms.length > 0 ? "configured" : "not_configured"}>
        <TabsList className="w-full">
          <TabsTrigger value="configured" className="flex-1">
            Configured ({configuredForms.length + orphanedConfigs.length})
          </TabsTrigger>
          <TabsTrigger value="not_configured" className="flex-1">
            Not Configured ({unconfiguredForms.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="configured" className="mt-3 space-y-2">
          {configuredForms.length === 0 && orphanedConfigs.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No forms configured yet. Go to "Not Configured" to set up a form.</p>
          ) : (
            <>
              {configuredForms.map((form) => {
                const cfg = configByFormId[form.id];
                return (
                  <div key={form.id} className="flex items-center justify-between rounded-lg border border-navy/10 bg-white p-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-navy truncate">{form.name}</p>
                        <FormLeadBadge form={form} />
                      </div>
                      {cfg.defaults && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {[cfg.defaults.stage, cfg.defaults.status, cfg.defaults.source].filter(Boolean).join(" / ")}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-2 shrink-0">
                      <Button
                        size="sm"
                        className="bg-[#1877F2] text-white hover:bg-[#166FE5] text-xs h-8"
                        onClick={() => onConfigureForm(form, cfg)}
                      >
                        Configure
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-8"
                        disabled={exportingId === form.id}
                        onClick={() => handleExportLeads(form.id, form.name)}
                        title="Download all leads for this form as Excel"
                      >
                        {exportingId === form.id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Download className="h-3.5 w-3.5" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="text-xs h-8"
                        onClick={() => handleDelete(cfg.id, form.name)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
              {orphanedConfigs.map((cfg) => (
                <div key={cfg.id} className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50/50 p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy truncate">{cfg.form_name}</p>
                    <p className="text-xs text-red-600 mt-0.5">Form not found on Facebook</p>
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="text-xs h-8 ml-2 shrink-0"
                    onClick={() => handleDelete(cfg.id, cfg.form_name)}
                  >
                    Delete
                  </Button>
                </div>
              ))}
            </>
          )}
        </TabsContent>

        <TabsContent value="not_configured" className="mt-3 space-y-2">
          {unconfiguredForms.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {q ? "No forms match your search." : "All forms are configured."}
            </p>
          ) : (
            unconfiguredForms.map((form) => (
              <div key={form.id} className="flex items-center justify-between rounded-lg border border-navy/10 bg-white p-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-navy truncate">{form.name}</p>
                    <FormLeadBadge form={form} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {form.status === "ARCHIVED" ? "Archived" : "Active"}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-8"
                    disabled={exportingId === form.id}
                    onClick={() => handleExportLeads(form.id, form.name)}
                    title="Download all leads for this form as Excel"
                  >
                    {exportingId === form.id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Download className="h-3.5 w-3.5" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-8 border-green-300 text-green-700 hover:bg-green-50"
                    onClick={() => onConfigureForm(form, null)}
                  >
                    Connect
                  </Button>
                </div>
              </div>
            ))
          )}
        </TabsContent>
      </Tabs>

      <div className="rounded-lg border border-green-200 bg-green-50 p-3">
        <p className="text-xs text-green-800">
          <span className="font-semibold">Tip:</span> Configured forms will use your chosen defaults (lead owner, source, stage, status, branch) for every incoming lead. Unconfigured forms still work — they use default settings.
        </p>
      </div>
    </div>
  );
}


function SyncMissedDialog({ open, onOpenChange, pageId, forms, configuredFormIds = [] }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [rangeMode, setRangeMode] = useState("days"); // "days" | "custom"
  const [days, setDays] = useState(14);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState(todayStr);
  const [scope, setScope] = useState("configured"); // "configured" | "all" | "some"
  const [selectedForms, setSelectedForms] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const [markToday, setMarkToday] = useState(false); // false = real FB date
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    api.get("/users", { params: { department: "pre_sales" } })
      .then((r) => setUsers(r.data)).catch(() => {});
  }, [open]);

  const buildBody = (dryRun) => {
    const body = {
      date_mode: markToday ? "today" : "real",
      dry_run: dryRun,
    };
    if (rangeMode === "custom") {
      if (dateFrom) body.date_from = dateFrom;
      if (dateTo) body.date_to = dateTo;
    } else {
      body.days = Number(days) || 14;
    }
    if (scope === "some") body.form_ids = selectedForms;
    else if (scope === "configured") body.form_ids = configuredFormIds;
    // scope === "all" → omit form_ids → backend scans every form
    if (assignTo) body.assign_to = assignTo;
    return body;
  };

  const run = async (dryRun) => {
    if (scope === "some" && selectedForms.length === 0) {
      toast.error("Select at least one form, or choose another scope");
      return;
    }
    if (scope === "configured" && configuredFormIds.length === 0) {
      toast.error("No configured forms yet — choose 'All forms' or configure some first");
      return;
    }
    setBusy(true);
    try {
      // A real sync fetches every lead one-by-one from Facebook and can take
      // several minutes for a large window — override the default axios timeout.
      const res = await api.post(
        `/integrations/meta/pages/${pageId}/backfill`,
        buildBody(dryRun),
        { timeout: 600000 },
      );
      const d = res.data || {};
      if (dryRun) {
        setPreview(d);
      } else {
        toast.success(
          `Sync complete — ${d.created || 0} imported, ${d.repeats || 0} repeat, ${d.skipped || 0} already in CRM`,
          { duration: 8000 }
        );
        if (d.errors?.length) toast.warning(`${d.errors.length} form(s) had errors — check server logs`);
        onOpenChange(false);
      }
    } catch (err) {
      const status = err.response?.status;
      const timedOut = err.code === "ECONNABORTED" || err.message?.includes("timeout")
        || status === 502 || status === 504;
      if (timedOut) {
        // Backend keeps running even though the connection dropped — the sync is
        // NOT actually failed. Tell the user honestly and let them re-check.
        toast.info(
          "Sync is still running on the server (large batches take a few minutes). Leads will keep appearing — re-check in a bit, or re-run to import the rest (no duplicates).",
          { duration: 12000 }
        );
        onOpenChange(false);
      } else if (status === 401) {
        toast.error("Page token expired — please reconnect this page");
      } else {
        toast.error(err.response?.data?.detail || "Sync failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const toggleForm = (id) =>
    setSelectedForms((prev) => prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Sync missed leads</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Date range */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-navy">Date range</Label>
            <div className="flex gap-2">
              <Button size="sm" variant={rangeMode === "days" ? "default" : "outline"}
                className="text-xs h-8 flex-1" onClick={() => setRangeMode("days")}>Last N days</Button>
              <Button size="sm" variant={rangeMode === "custom" ? "default" : "outline"}
                className="text-xs h-8 flex-1" onClick={() => setRangeMode("custom")}>Custom range</Button>
            </div>
            {rangeMode === "days" ? (
              <div className="flex items-center gap-2">
                <Input type="number" min="1" max="365" value={days}
                  onChange={(e) => setDays(e.target.value)} className="h-8 w-24 text-sm" />
                <span className="text-xs text-muted-foreground">days back from today</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input type="date" value={dateFrom} max={dateTo || todayStr}
                  onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-sm" />
                <span className="text-xs text-muted-foreground">to</span>
                <Input type="date" value={dateTo} max={todayStr}
                  onChange={(e) => setDateTo(e.target.value)} className="h-8 text-sm" />
              </div>
            )}
          </div>

          {/* Forms */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-navy">Forms</Label>
            <div className="flex gap-2">
              <Button size="sm" variant={scope === "configured" ? "default" : "outline"}
                className="text-xs h-8 flex-1" onClick={() => setScope("configured")}>Configured only ({configuredFormIds.length})</Button>
              <Button size="sm" variant={scope === "all" ? "default" : "outline"}
                className="text-xs h-8 flex-1" onClick={() => setScope("all")}>All forms</Button>
              <Button size="sm" variant={scope === "some" ? "default" : "outline"}
                className="text-xs h-8 flex-1" onClick={() => setScope("some")}>Specific</Button>
            </div>
            {scope === "configured" && (
              <p className="text-[11px] text-muted-foreground">Only your {configuredFormIds.length} configured form(s) will be synced.</p>
            )}
            {scope === "some" && (
              <div className="max-h-40 overflow-y-auto rounded-md border border-navy/10 p-2 space-y-1">
                {forms.length === 0 && <p className="text-xs text-muted-foreground px-1">No forms found.</p>}
                {forms.map((f) => (
                  <label key={f.id} className="flex items-center gap-2 text-xs px-1 py-0.5 cursor-pointer hover:bg-muted rounded">
                    <Checkbox checked={selectedForms.includes(f.id)} onCheckedChange={() => toggleForm(f.id)} />
                    <span className="truncate">{f.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Assign to */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-navy">Assign new leads to</Label>
            <Select value={assignTo || "__none__"} onValueChange={(v) => setAssignTo(v === "__none__" ? "" : v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Use form default / unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Use form default / unassigned</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">Applies to new leads only. Repeat leads keep their current owner.</p>
          </div>

          {/* Created date */}
          <div className="flex items-center justify-between rounded-md border border-navy/10 p-2.5">
            <div className="min-w-0 pr-2">
              <p className="text-xs font-semibold text-navy">Mark created date as today</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {markToday
                  ? "Leads appear at top of the queue as fresh (distorts date reports)."
                  : "Off = use the real Facebook submit date (accurate reporting)."}
              </p>
            </div>
            <Switch checked={markToday} onCheckedChange={setMarkToday} />
          </div>

          {/* Preview result */}
          {preview && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
              <p className="font-semibold mb-1">Preview (nothing imported yet)</p>
              <p>
                <b>{preview.created}</b> new · <b>{preview.repeats}</b> repeat (already a lead — same phone) · <b>{preview.skipped}</b> already imported · {preview.fetched} scanned across {preview.forms_scanned} form(s)
              </p>
              <p className="mt-1 text-blue-700">
                Run sync will add {preview.created} new lead(s); the {preview.repeats} repeat(s) get a repeat-enquiry note (no duplicates).
              </p>
              {preview.errors?.length > 0 && <p className="text-red-600 mt-1">{preview.errors.length} form(s) errored</p>}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="text-xs h-8" disabled={busy} onClick={() => run(true)}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Preview"}
          </Button>
          <Button className="bg-navy text-white hover:bg-navy-hover text-xs h-8" disabled={busy} onClick={() => run(false)}>
            {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Working…</> : "Run sync"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function FormConfigWizard({ page, form, existingConfig, onSaved, onBack }) {
  const { sources, stages, statuses, categories, concerns, services } = useMasterData();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Step 0: Default Parameters
  const [defaults, setDefaults] = useState({
    assigned_to: existingConfig?.defaults?.assigned_to || "",
    source: existingConfig?.defaults?.source || "Facebook",
    stage: existingConfig?.defaults?.stage || "hot",
    status: existingConfig?.defaults?.status || "new_lead",
    branch_id: existingConfig?.defaults?.branch_id || "",
    franchise_id: existingConfig?.defaults?.franchise_id || "",
    category: existingConfig?.defaults?.category || "",
    concern: existingConfig?.defaults?.concern || "",
    interested_service: existingConfig?.defaults?.interested_service || "",
  });

  // Step 1: Field Mapping
  const [fieldMapping, setFieldMapping] = useState([]);
  const [formFields, setFormFields] = useState([]);

  // Dropdowns data
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = { department: "pre_sales" };
    if (defaults.branch_id) params.branch_id = defaults.branch_id;
    api.get("/users", { params }).then((r) => setUsers(r.data)).catch(() => {});
  }, [defaults.branch_id]);

  // A saved owner who is no longer in the selectable list has been deactivated (or
  // moved out of pre-sales): the Select renders blank and the form looks unowned
  // for no stated reason. Wait for the list to load before judging.
  const ownerInactive = Boolean(
    defaults.assigned_to && users.length > 0 && !users.some((u) => u.id === defaults.assigned_to)
  );

  // Fetch form fields from Meta to build mapping
  useEffect(() => {
    if (existingConfig?.field_mapping?.length) {
      setFieldMapping(existingConfig.field_mapping);
      setFormFields(existingConfig.field_mapping.map((m) => m.form_field));
    } else {
      // Fetch a sample lead to discover form fields
      api.get(`/integrations/meta/pages/${page.page_id}/forms`)
        .then((r) => {
          const thisForm = r.data.find((f) => f.id === form.id);
          if (thisForm?.questions) {
            const fields = thisForm.questions.map((q) => q.key || q.label);
            setFormFields(fields);
            setFieldMapping(fields.map((f) => ({
              form_field: f,
              crm_field: AUTO_MAP[f] || "",
              enabled: !!AUTO_MAP[f],
            })));
          } else {
            const defaultFields = ["full_name", "phone_number", "email", "city"];
            setFormFields(defaultFields);
            setFieldMapping(defaultFields.map((f) => ({
              form_field: f,
              crm_field: AUTO_MAP[f] || "",
              enabled: !!AUTO_MAP[f],
            })));
          }
        })
        .catch(() => {
          const defaultFields = ["full_name", "phone_number", "email", "city"];
          setFormFields(defaultFields);
          setFieldMapping(defaultFields.map((f) => ({
            form_field: f,
            crm_field: AUTO_MAP[f] || "",
            enabled: !!AUTO_MAP[f],
          })));
        });
    }
  }, []);

  const setDefault = (key, value) => setDefaults((prev) => ({ ...prev, [key]: value }));

  const updateMapping = (index, field, value) => {
    setFieldMapping((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        page_id: page.page_id,
        form_id: form.id,
        form_name: form.name,
        defaults: {
          ...defaults,
          assigned_to: defaults.assigned_to || null,
          branch_id: defaults.branch_id || null,
          franchise_id: defaults.franchise_id || null,
        },
        field_mapping: fieldMapping,
      };

      if (existingConfig?.id) {
        await api.put(`/integrations/meta/form-configs/${existingConfig.id}`, {
          defaults: body.defaults,
          field_mapping: body.field_mapping,
        });
      } else {
        await api.post("/integrations/meta/form-configs", body);
      }
      toast.success("Form configuration saved!");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 py-2">
      {/* Step indicator */}
      <div className="flex items-center justify-center gap-4">
        {["Default Parameters", "Map Columns"].map((label, i) => (
          <button
            key={label}
            onClick={() => setStep(i)}
            className={`flex items-center gap-2 text-xs font-medium transition-colors ${
              step === i ? "text-navy" : "text-muted-foreground hover:text-navy/70"
            }`}
          >
            <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
              step === i ? "bg-navy text-white" : step > i ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
            }`}>
              {step > i ? <Check className="h-3 w-3" /> : i + 1}
            </span>
            {label}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-navy/10 bg-cream/30 px-3 py-2">
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-navy">{form.name}</span> — {page.page_name}
        </p>
      </div>

      {step === 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-navy">Lead Owner</Label>
              <Select value={defaults.assigned_to} onValueChange={(v) => setDefault("assigned_to", v)}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Round-robin across tele-callers" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Leaving this blank used to mean "nobody" — the leads arrived unowned
                  and stayed that way. It now means round-robin, so say which it is. */}
              {ownerInactive ? (
                <p className="text-[11px] text-amber-700">
                  This form&apos;s owner is deactivated — its leads are going to the round-robin
                  pool. Pick an active owner to route them deliberately.
                </p>
              ) : !defaults.assigned_to ? (
                <p className="text-[11px] text-muted-foreground">
                  Leave blank to share this form&apos;s leads round-robin across active tele-callers.
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-navy">Source</Label>
              <Select value={defaults.source} onValueChange={(v) => setDefault("source", v)}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {sources.map((s) => (
                    <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-navy">Sub Source</Label>
              <div className="flex h-9 items-center rounded-md border border-navy/10 bg-cream/30 px-3 text-xs text-muted-foreground">
                <span className="truncate font-medium text-navy">{form.name}</span>
              </div>
              <p className="text-[10px] text-muted-foreground">Auto-set to the form name for every lead from this form.</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-navy">Stage</Label>
              <Select value={defaults.stage} onValueChange={(v) => setDefault("stage", v)}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-navy">Status</Label>
              <Select value={defaults.status} onValueChange={(v) => setDefault("status", v)}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-navy">Branch</Label>
              <Select value={defaults.branch_id} onValueChange={(v) => setDefault("branch_id", v)}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="No branch (auto)" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id} className="text-xs">{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-navy">Category</Label>
              <Select value={defaults.category} onValueChange={(v) => setDefault("category", v)}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-navy">Concern</Label>
              <Select value={defaults.concern} onValueChange={(v) => setDefault("concern", v)}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  {concerns.map((c) => (
                    <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-navy">Interested Service</Label>
              <Select value={defaults.interested_service} onValueChange={(v) => setDefault("interested_service", v)}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => setStep(1)} className="bg-navy text-white hover:bg-navy-hover">
              Next
            </Button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">Map Facebook form fields to CRM lead fields. Only enabled fields will be imported.</p>

          <div className="space-y-2">
            {fieldMapping.map((mapping, idx) => (
              <div key={idx} className="flex items-center gap-3 rounded-lg border border-navy/10 bg-white p-3">
                <Checkbox
                  checked={mapping.enabled}
                  onCheckedChange={(checked) => updateMapping(idx, "enabled", !!checked)}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-navy truncate">{humanizeFieldName(mapping.form_field)}</p>
                </div>
                <Select
                  value={mapping.crm_field}
                  onValueChange={(v) => updateMapping(idx, "crm_field", v)}
                  disabled={!mapping.enabled}
                >
                  <SelectTrigger className="h-8 w-44 text-xs">
                    <SelectValue placeholder="Select CRM field" />
                  </SelectTrigger>
                  <SelectContent>
                    {CRM_FIELDS.map((f) => (
                      <SelectItem key={f.value} value={f.value} className="text-xs">{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          {fieldMapping.length === 0 && (
            <p className="py-4 text-center text-xs text-muted-foreground">No form fields detected. Default field mapping will be used.</p>
          )}

          <div className="flex justify-between">
            <Button onClick={() => setStep(0)} variant="outline">
              Prev
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-navy text-white hover:bg-navy-hover"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function CampaignsList({ data }) {
  const { active = [], inactive = [] } = data;

  if (active.length === 0 && inactive.length === 0) {
    return <p className="py-2 text-center text-xs text-muted-foreground">No campaigns found for this account.</p>;
  }

  if (data.error) {
    return <p className="py-2 text-center text-xs text-red-600">{data.error}</p>;
  }

  return (
    <div className="space-y-3">
      {active.length > 0 && (
        <div>
          <p className="mb-1.5 text-xs font-semibold text-green-700">Active Campaigns ({active.length})</p>
          <div className="space-y-1.5">
            {active.map((c) => <CampaignRow key={c.id} campaign={c} />)}
          </div>
        </div>
      )}
      {inactive.length > 0 && (
        <div>
          <p className="mb-1.5 text-xs font-semibold text-muted-foreground">Inactive Campaigns ({inactive.length})</p>
          <div className="space-y-1.5">
            {inactive.map((c) => <CampaignRow key={c.id} campaign={c} />)}
          </div>
        </div>
      )}
    </div>
  );
}


function CampaignRow({ campaign }) {
  const statusColor = campaign.status === "ACTIVE" ? "bg-green-100 text-green-800"
    : campaign.status === "PAUSED" ? "bg-amber-100 text-amber-800"
    : "bg-gray-100 text-gray-600";

  const budget = campaign.daily_budget
    ? `₹${(parseInt(campaign.daily_budget, 10) / 100).toLocaleString()}/day`
    : campaign.lifetime_budget
    ? `₹${(parseInt(campaign.lifetime_budget, 10) / 100).toLocaleString()} lifetime`
    : "";

  return (
    <div className="flex items-center gap-2 rounded-md border border-navy/5 bg-cream/30 px-3 py-2">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-navy truncate">{campaign.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {campaign.objective && <span className="text-[10px] text-muted-foreground">{campaign.objective.replace(/_/g, " ")}</span>}
          {budget && <span className="text-[10px] text-muted-foreground">{budget}</span>}
        </div>
      </div>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusColor}`}>
        {campaign.status}
      </span>
    </div>
  );
}
